import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API_BASE, formatApiError, useAuth } from "../components/AuthContext";
import {
  Contact2, Plus, Trash2, Loader2, Save, X as XIcon, Pencil, Mail, Phone,
  Building2, ExternalLink, Tag, Copy, KeyRound, Globe, Eye,
} from "lucide-react";

const INPUT = "w-full bg-white border border-[var(--border)] rounded-md px-2.5 py-1.5 text-[13px] focus:outline-none focus:border-[var(--ink)]";

export default function ReferralPartners() {
  const { state } = useAuth();
  const isOwner = state?.user?.role === "owner";
  const [tab, setTab] = useState("partners");
  return (
    <div className="px-10 py-12 max-w-[1240px]" data-testid="referral-partners-page">
      <div className="mb-6">
        <div className="overline mb-2">Directory · Referral partners</div>
        <h1 className="font-heading text-4xl" style={{ fontWeight: 500 }}>
          Referral <em className="italic" style={{ color: "var(--accent)" }}>partners</em>
        </h1>
        <p className="text-[var(--ink-2)] mt-2 max-w-2xl">
          Keep the mortgage brokers, insurance brokers, property valuers and everyone else you
          refer to in one place. Team members can look up contact details; owners maintain the list.
        </p>
      </div>

      <div className="inline-flex rounded-md border border-[var(--border)] bg-white p-1 mb-6"
           role="tablist" data-testid="referral-tabs">
        <button onClick={() => setTab("partners")}
                data-testid="referral-tab-partners"
                aria-selected={tab === "partners"}
                className={`px-4 py-1.5 text-[12px] rounded ${tab === "partners"
                  ? "bg-[var(--ink)] text-[var(--paper)]"
                  : "text-[var(--ink-2)] hover:text-[var(--ink)]"}`}>
          Partners
        </button>
        <button onClick={() => setTab("types")}
                data-testid="referral-tab-types"
                aria-selected={tab === "types"}
                className={`px-4 py-1.5 text-[12px] rounded ${tab === "types"
                  ? "bg-[var(--ink)] text-[var(--paper)]"
                  : "text-[var(--ink-2)] hover:text-[var(--ink)]"}`}>
          Types
        </button>
        <button onClick={() => setTab("templates")}
                data-testid="referral-tab-templates"
                aria-selected={tab === "templates"}
                className={`px-4 py-1.5 text-[12px] rounded ${tab === "templates"
                  ? "bg-[var(--ink)] text-[var(--paper)]"
                  : "text-[var(--ink-2)] hover:text-[var(--ink)]"}`}>
          Email templates
        </button>
      </div>

      {tab === "partners" && <PartnersTable isOwner={isOwner} />}
      {tab === "types"    && <TypesTable    isOwner={isOwner} />}
      {tab === "templates"&& <TemplatesTable isOwner={isOwner} />}
    </div>
  );
}


function TypesTable({ isOwner }) {
  const [rows, setRows] = useState([]);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);

  const reload = async () => {
    try {
      const r = await axios.get(`${API_BASE}/referral-partner-types`);
      setRows(r.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { reload(); }, []);

  const add = async () => {
    if (!draft.trim()) return;
    setBusy(true);
    try {
      await axios.post(`${API_BASE}/referral-partner-types`, { name: draft.trim() });
      setDraft("");
      toast.success("Type added");
      reload();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Remove type "${t.name}"? Active partners using it must be reassigned first.`)) return;
    try {
      await axios.delete(`${API_BASE}/referral-partner-types/${t.type_id}`);
      toast.success("Type removed");
      reload();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="card p-5" data-testid="referral-types-card">
      {isOwner && (
        <div className="flex items-center gap-2 mb-4" data-testid="referral-type-new-row">
          <Tag size={14} className="text-[var(--ink-3)]" />
          <input value={draft} onChange={(e) => setDraft(e.target.value)}
                 onKeyDown={(e) => e.key === "Enter" && add()}
                 placeholder="New type — e.g. Mortgage Broker"
                 className={INPUT + " max-w-[320px]"}
                 data-testid="referral-type-name-input" />
          <button onClick={add} disabled={busy || !draft.trim()} className="btn-primary text-[12px]"
                  data-testid="referral-type-add-btn">
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />} Add
          </button>
        </div>
      )}
      {rows.length === 0 ? (
        <div className="text-[13px] text-[var(--ink-3)]">
          {isOwner ? "No types yet. Add your first one above." : "No referral partner types yet."}
        </div>
      ) : (
        <ul className="divide-y divide-[var(--border)]">
          {rows.map((t) => (
            <li key={t.type_id} className="flex items-center justify-between py-2"
                data-testid={`referral-type-row-${t.type_id}`}>
              <span className="text-[13px] text-[var(--ink)]">{t.name}</span>
              {isOwner && (
                <button onClick={() => remove(t)}
                        className="text-[var(--ink-3)] hover:text-[var(--accent)]"
                        data-testid={`referral-type-delete-${t.type_id}`}>
                  <Trash2 size={13} />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}


function PartnersTable({ isOwner }) {
  const [rows, setRows] = useState([]);
  const [types, setTypes] = useState([]);
  const [filterType, setFilterType] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const reload = async () => {
    try {
      const [p, t] = await Promise.all([
        axios.get(`${API_BASE}/referral-partners${filterType ? `?type_id=${filterType}` : ""}`),
        axios.get(`${API_BASE}/referral-partner-types`),
      ]);
      setRows(p.data || []);
      setTypes(t.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [filterType]);

  const remove = async (p) => {
    if (!window.confirm(`Deactivate ${p.name}? Existing referrals stay intact but they won't appear when adding new ones.`)) return;
    try {
      await axios.delete(`${API_BASE}/referral-partners/${p.partner_id}`);
      toast.success("Partner deactivated");
      reload();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <select value={filterType} onChange={(e) => setFilterType(e.target.value)}
                className={INPUT + " max-w-[240px]"}
                data-testid="referral-partner-filter-type">
          <option value="">All types</option>
          {types.map((t) => (
            <option key={t.type_id} value={t.type_id}>{t.name}</option>
          ))}
        </select>
        {isOwner && (
          <button className="btn-primary" onClick={() => { setEditing(null); setModalOpen(true); }}
                  data-testid="referral-partner-new-btn">
            <Plus size={13} /> New partner
          </button>
        )}
      </div>

      {rows.length === 0 ? (
        <div className="card p-10 text-center" data-testid="referral-partners-empty">
          <Contact2 size={26} className="mx-auto mb-3 text-[var(--ink-3)]" />
          <div className="font-heading text-xl mb-1" style={{ fontWeight: 500 }}>No referral partners yet</div>
          <p className="text-[var(--ink-3)] text-[13px]">
            {isOwner ? 'Add a type first (e.g. "Mortgage Broker"), then add partners under it.'
                     : "Ask a firm owner to set up the referral directory."}
          </p>
        </div>
      ) : (
        <div className="card overflow-hidden">
          <table className="report-table">
            <thead><tr><th>Name</th><th>Type</th><th>Company</th><th>Contact</th><th>Active refs</th><th></th></tr></thead>
            <tbody>
              {rows.map((p) => (
                <tr key={p.partner_id} data-testid={`referral-partner-row-${p.partner_id}`}>
                  <td className="font-medium text-[var(--ink)]">{p.name}</td>
                  <td className="text-[13px]">{p.type_name || "—"}</td>
                  <td className="text-[13px] text-[var(--ink-2)]">{p.company || "—"}</td>
                  <td className="text-[13px]">
                    <div className="flex flex-col gap-0.5">
                      {p.email && (
                        <a href={`mailto:${p.email}`} className="text-[var(--accent)] hover:underline inline-flex items-center gap-1"
                           data-testid={`referral-partner-email-${p.partner_id}`}>
                          <Mail size={10} /> {p.email}
                        </a>
                      )}
                      {p.phone && (
                        <a href={`tel:${p.phone}`} className="text-[var(--ink-2)] hover:text-[var(--accent)] inline-flex items-center gap-1"
                           data-testid={`referral-partner-phone-${p.partner_id}`}>
                          <Phone size={10} /> {p.phone}
                        </a>
                      )}
                      {p.portal_url && (
                        <PortalChips partner={p} />
                      )}
                      {!p.email && !p.phone && !p.portal_url && <span className="text-[var(--ink-3)]">—</span>}
                    </div>
                  </td>
                  <td className="text-[13px] text-[var(--ink-2)]">{p.active_referral_count || 0}</td>
                  <td>
                    {isOwner && (
                      <div className="flex items-center gap-2">
                        <button onClick={() => { setEditing(p); setModalOpen(true); }}
                                className="text-[var(--ink-3)] hover:text-[var(--accent)]"
                                data-testid={`referral-partner-edit-${p.partner_id}`}>
                          <Pencil size={12} />
                        </button>
                        <button onClick={() => remove(p)}
                                className="text-[var(--ink-3)] hover:text-[var(--accent)]"
                                data-testid={`referral-partner-delete-${p.partner_id}`}>
                          <Trash2 size={12} />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modalOpen && (
        <PartnerModal
          types={types}
          initial={editing}
          onClose={() => setModalOpen(false)}
          onSaved={() => { setModalOpen(false); reload(); }}
        />
      )}
    </div>
  );
}


function PartnerModal({ types, initial, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: initial?.name || "",
    type_id: initial?.type_id || types[0]?.type_id || "",
    company: initial?.company || "",
    email: initial?.email || "",
    phone: initial?.phone || "",
    website: initial?.website || "",
    address: initial?.address || "",
    notes: initial?.notes || "",
    // v68.37 — partner portal registry (password Fernet-encrypted at rest)
    portal_url: initial?.portal_url || "",
    portal_username: initial?.portal_username || "",
    portal_password: "",   // never pre-fills (masked); patch is optional
    portal_notes: initial?.portal_notes || "",
  });
  const [busy, setBusy] = useState(false);
  const upd = (patch) => setForm((f) => ({ ...f, ...patch }));

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.type_id) return;
    setBusy(true);
    try {
      const body = { ...form };
      Object.keys(body).forEach((k) => { if (body[k] === "") delete body[k]; });
      if (initial) {
        await axios.patch(`${API_BASE}/referral-partners/${initial.partner_id}`, body);
        toast.success("Partner updated");
      } else {
        await axios.post(`${API_BASE}/referral-partners`, body);
        toast.success("Partner created");
      }
      onSaved();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
         onClick={onClose} data-testid="referral-partner-modal">
      <form onClick={(e) => e.stopPropagation()} onSubmit={save}
            className="bg-white rounded-lg p-6 w-full max-w-lg space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-heading text-xl">{initial ? "Edit partner" : "New referral partner"}</h3>
          <button type="button" onClick={onClose} className="text-stone-400 hover:text-stone-700">
            <XIcon size={18} />
          </button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <label className="overline block mb-1">Name *</label>
            <input required value={form.name} onChange={(e) => upd({ name: e.target.value })}
                   className={INPUT} data-testid="referral-partner-input-name" />
          </div>
          <div>
            <label className="overline block mb-1">Type *</label>
            <select required value={form.type_id} onChange={(e) => upd({ type_id: e.target.value })}
                    className={INPUT} data-testid="referral-partner-input-type">
              {types.map((t) => <option key={t.type_id} value={t.type_id}>{t.name}</option>)}
            </select>
          </div>
          <div>
            <label className="overline block mb-1">Company</label>
            <input value={form.company} onChange={(e) => upd({ company: e.target.value })}
                   className={INPUT} data-testid="referral-partner-input-company" />
          </div>
          <div>
            <label className="overline block mb-1">Email</label>
            <input type="email" value={form.email} onChange={(e) => upd({ email: e.target.value })}
                   className={INPUT} data-testid="referral-partner-input-email" />
          </div>
          <div>
            <label className="overline block mb-1">Phone</label>
            <input value={form.phone} onChange={(e) => upd({ phone: e.target.value })}
                   className={INPUT} data-testid="referral-partner-input-phone" />
          </div>
          <div className="col-span-2">
            <label className="overline block mb-1">Website</label>
            <input value={form.website} onChange={(e) => upd({ website: e.target.value })}
                   placeholder="https://" className={INPUT} />
          </div>
          <div className="col-span-2">
            <label className="overline block mb-1">Address</label>
            <input value={form.address} onChange={(e) => upd({ address: e.target.value })}
                   className={INPUT} />
          </div>

          {/* v68.37 — partner portal login (optional). Password is Fernet-encrypted server-side. */}
          <div className="col-span-2 pt-2 border-t border-[var(--border)]">
            <div className="overline mb-2 inline-flex items-center gap-1">
              <KeyRound size={11}/> Partner portal <span className="text-[var(--ink-3)]">(optional)</span>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="col-span-2">
                <label className="text-[11px] text-[var(--ink-3)] block mb-1">Portal URL</label>
                <input value={form.portal_url} onChange={(e) => upd({ portal_url: e.target.value })}
                       placeholder="https://portal.aussielend.example"
                       className={INPUT} data-testid="referral-partner-input-portal-url" />
              </div>
              <div>
                <label className="text-[11px] text-[var(--ink-3)] block mb-1">Username</label>
                <input value={form.portal_username} onChange={(e) => upd({ portal_username: e.target.value })}
                       className={INPUT} data-testid="referral-partner-input-portal-username" />
              </div>
              <div>
                <label className="text-[11px] text-[var(--ink-3)] block mb-1">
                  Password {initial?.has_portal_password && <span className="text-[var(--moss)]">· saved</span>}
                </label>
                <input type="password" value={form.portal_password}
                       onChange={(e) => upd({ portal_password: e.target.value })}
                       placeholder={initial?.has_portal_password ? "•••• (leave blank to keep)" : ""}
                       className={INPUT} data-testid="referral-partner-input-portal-password" />
              </div>
              <div className="col-span-2">
                <label className="text-[11px] text-[var(--ink-3)] block mb-1">Login notes (MFA, contact for reset, ...)</label>
                <textarea rows={2} value={form.portal_notes}
                          onChange={(e) => upd({ portal_notes: e.target.value })}
                          className={INPUT} data-testid="referral-partner-input-portal-notes" />
              </div>
            </div>
            <div className="text-[10px] text-[var(--ink-3)] mt-1.5">
              Password is encrypted at rest with Fernet. Each reveal is logged in your audit trail.
            </div>
          </div>

          <div className="col-span-2">
            <label className="overline block mb-1">Notes</label>
            <textarea rows={3} value={form.notes} onChange={(e) => upd({ notes: e.target.value })}
                      className={INPUT} />
          </div>
        </div>
        <div className="flex gap-3 pt-2">
          <button type="button" onClick={onClose} className="btn-secondary flex-1 justify-center">Cancel</button>
          <button type="submit" disabled={busy || !form.name.trim() || !form.type_id}
                  className="btn-primary flex-1 justify-center"
                  data-testid="referral-partner-save-btn">
            {busy ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
            {initial ? "Save" : "Create"}
          </button>
        </div>
      </form>
    </div>
  );
}


// v68.37 — Portal login chips. Reveal fetches the decrypted password from
// `/portal` (which writes an audit_log row) and copies it to the clipboard.

// v68.38 — Referral email templates. Owner-only writes; staff read-only.
// Optional `partner_type_id` scopes a template to one type; null = universal.
// Body/subject support `{{lead_name}}`, `{{partner_first_name}}`, etc.
function TemplatesTable({ isOwner }) {
  const [rows, setRows] = useState([]);
  const [types, setTypes] = useState([]);
  const [modal, setModal] = useState(null);   // null | initial-doc | {} for new

  const reload = async () => {
    try {
      const [t, ty] = await Promise.all([
        axios.get(`${API_BASE}/referral-email-templates`),
        axios.get(`${API_BASE}/referral-partner-types`),
      ]);
      setRows(t.data || []);
      setTypes(ty.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { reload(); }, []);

  const remove = async (tpl) => {
    if (!window.confirm(`Delete template "${tpl.name}"?`)) return;
    try {
      await axios.delete(`${API_BASE}/referral-email-templates/${tpl.template_id}`);
      toast.success("Template deleted");
      reload();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const typeNameById = Object.fromEntries(types.map((t) => [t.type_id, t.name]));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-[12px] text-[var(--ink-3)]">
          Reusable intro-email bodies. Supports{" "}
          <span className="font-mono text-[10px]">{"{{lead_name}}"}, {"{{partner_first_name}}"}, {"{{lead_email}}"}, {"{{lead_phone}}"}, {"{{lead_notes}}"}, {"{{lead_source}}"}</span>.
        </div>
        {isOwner && (
          <button onClick={() => setModal({})} className="btn-primary text-[12px]"
                  data-testid="referral-template-new-btn">
            <Plus size={12}/> New template
          </button>
        )}
      </div>

      {rows.length === 0 ? (
        <div className="card p-10 text-center" data-testid="referral-templates-empty">
          <Contact2 size={26} className="mx-auto mb-3 text-[var(--ink-3)]" />
          <div className="font-heading text-xl mb-1" style={{ fontWeight: 500 }}>No templates yet</div>
          <p className="text-[var(--ink-3)] text-[13px]">
            {isOwner ? "Create your first intro-email template to speed up referrals."
                     : "Ask an owner to create referral email templates."}
          </p>
        </div>
      ) : (
        <div className="card overflow-hidden">
          <table className="report-table">
            <thead><tr><th>Name</th><th>Scope</th><th>Subject preview</th><th></th></tr></thead>
            <tbody>
              {rows.map((t) => (
                <tr key={t.template_id} data-testid={`referral-template-row-${t.template_id}`}>
                  <td className="font-medium text-[var(--ink)]">{t.name}</td>
                  <td className="text-[13px] text-[var(--ink-2)]">
                    {t.partner_type_id ? (typeNameById[t.partner_type_id] || "—")
                                       : <span className="text-[var(--ink-3)] italic">Any type</span>}
                  </td>
                  <td className="text-[13px] text-[var(--ink-2)] truncate max-w-[420px]">
                    {t.subject_template}
                  </td>
                  <td>
                    {isOwner && (
                      <div className="flex items-center gap-2">
                        <button onClick={() => setModal(t)}
                                className="text-[var(--ink-3)] hover:text-[var(--accent)]"
                                data-testid={`referral-template-edit-${t.template_id}`}>
                          <Pencil size={12}/>
                        </button>
                        <button onClick={() => remove(t)}
                                className="text-[var(--ink-3)] hover:text-[var(--accent)]"
                                data-testid={`referral-template-delete-${t.template_id}`}>
                          <Trash2 size={12}/>
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modal !== null && (
        <TemplateModal
          types={types}
          initial={modal?.template_id ? modal : null}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); reload(); }}
        />
      )}
    </div>
  );
}


function TemplateModal({ types, initial, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: initial?.name || "",
    partner_type_id: initial?.partner_type_id || "",
    subject_template: initial?.subject_template
      || "Referral introduction — {{lead_name}}",
    body_template: initial?.body_template ||
`Hi {{partner_first_name}},

I'd like to refer a client of ours who may benefit from your services.

• Name: {{lead_name}}
• Email: {{lead_email}}
• Phone: {{lead_phone}}

Please let me know the best way to introduce you.

Thanks,`,
  });
  const [busy, setBusy] = useState(false);
  const upd = (p) => setForm((f) => ({ ...f, ...p }));

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.subject_template.trim() || !form.body_template.trim()) return;
    setBusy(true);
    try {
      const body = { ...form };
      if (!body.partner_type_id) body.partner_type_id = null;
      if (initial) {
        await axios.patch(`${API_BASE}/referral-email-templates/${initial.template_id}`, body);
        toast.success("Template updated");
      } else {
        await axios.post(`${API_BASE}/referral-email-templates`, body);
        toast.success("Template created");
      }
      onSaved();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
         onClick={onClose} data-testid="referral-template-modal">
      <form onClick={(e) => e.stopPropagation()} onSubmit={save}
            className="bg-white rounded-lg p-6 w-full max-w-2xl space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-heading text-xl">{initial ? "Edit template" : "New email template"}</h3>
          <button type="button" onClick={onClose} className="text-stone-400 hover:text-stone-700">
            <XIcon size={18} />
          </button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="overline block mb-1">Name *</label>
            <input required value={form.name} onChange={(e) => upd({ name: e.target.value })}
                   className={INPUT} data-testid="referral-template-input-name" />
          </div>
          <div>
            <label className="overline block mb-1">Scope</label>
            <select value={form.partner_type_id}
                    onChange={(e) => upd({ partner_type_id: e.target.value })}
                    className={INPUT} data-testid="referral-template-input-type">
              <option value="">Any type</option>
              {types.map((t) => <option key={t.type_id} value={t.type_id}>{t.name}</option>)}
            </select>
          </div>
        </div>
        <div>
          <label className="overline block mb-1">Subject *</label>
          <input required value={form.subject_template}
                 onChange={(e) => upd({ subject_template: e.target.value })}
                 className={INPUT} data-testid="referral-template-input-subject" />
        </div>
        <div>
          <label className="overline block mb-1">Body *</label>
          <textarea required rows={12} value={form.body_template}
                    onChange={(e) => upd({ body_template: e.target.value })}
                    className={INPUT + " font-mono text-[12px]"}
                    data-testid="referral-template-input-body" />
        </div>
        <div className="flex gap-3 pt-2">
          <button type="button" onClick={onClose} className="btn-secondary flex-1 justify-center">Cancel</button>
          <button type="submit" disabled={busy} className="btn-primary flex-1 justify-center"
                  data-testid="referral-template-save-btn">
            {busy ? <Loader2 size={13} className="animate-spin"/> : <Save size={13}/>}
            {initial ? "Save" : "Create"}
          </button>
        </div>
      </form>
    </div>
  );
}


function PortalChips({ partner }) {
  const [busy, setBusy] = useState(false);

  const openPortal = () => {
    window.open(partner.portal_url, "_blank", "noopener,noreferrer");
  };

  const copyLogin = async () => {
    if (!partner.has_portal_password && !partner.portal_username) {
      toast.info("No credentials saved yet");
      return;
    }
    setBusy(true);
    try {
      const r = await axios.get(`${API_BASE}/referral-partners/${partner.partner_id}/portal`);
      const { portal_username: u, portal_password: p } = r.data || {};
      const parts = [];
      if (u) parts.push(`Username: ${u}`);
      if (p) parts.push(`Password: ${p}`);
      await navigator.clipboard.writeText(parts.join("\n"));
      toast.success("Login copied — reveal audited");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="inline-flex items-center gap-1.5"
         data-testid={`referral-partner-portal-chips-${partner.partner_id}`}>
      <button onClick={openPortal}
              className="text-[10px] inline-flex items-center gap-0.5 text-[var(--accent)] hover:underline"
              data-testid={`referral-partner-portal-open-${partner.partner_id}`}
              title="Open partner portal in a new tab">
        <Globe size={10}/> Portal <ExternalLink size={8}/>
      </button>
      {(partner.has_portal_password || partner.portal_username) && (
        <button onClick={copyLogin} disabled={busy}
                className="text-[10px] inline-flex items-center gap-0.5 text-[var(--ink-3)] hover:text-[var(--accent)]"
                data-testid={`referral-partner-portal-copy-${partner.partner_id}`}
                title="Copy login (audited)">
          {busy ? <Loader2 size={10} className="animate-spin"/> : <Copy size={10}/>} Login
        </button>
      )}
    </div>
  );
}

